/**
 * CTA Banner Component
 * Shows persistent banner for unauthenticated users encouraging signup
 */

import React, { useState } from 'react';
import { X, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.tsx';
import Button from './ui/Button';
import WaitlistModal from './WaitlistModal';

const BetaBanner = () => {
  const { user, authReady } = useAuth();
  const loading = !authReady;
  const isPro = user?.isPro || false;
  const [isDismissed, setIsDismissed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Don't show banner if:
  // - Still loading
  // - User is authenticated and has Pro access
  // - User has dismissed the banner
  if (loading || (user && isPro) || isDismissed) {
    return null;
  }

  // Show different messages based on auth status
  const getBannerContent = () => {
    if (user && !isPro) {
      // Authenticated but free account
      return {
        title: "You're using TrySnowball Free!",
        subtitle: "Upgrade to Pro for unlimited features.",
        ctaText: "Upgrade to Pro",
        bgColor: "bg-purple-50",
        borderColor: "border-purple-200",
        textColor: "text-purple-800",
        buttonColor: "bg-purple-600 hover:bg-purple-700"
      };
    } else {
      // Unauthenticated user
      return {
        title: "Track your debt elimination journey with TrySnowball.",
        subtitle: "Get started free!",
        ctaText: "Sign Up Free",
        bgColor: "bg-blue-50",
        borderColor: "border-blue-200", 
        textColor: "text-blue-800",
        buttonColor: "bg-blue-600 hover:bg-blue-700"
      };
    }
  };

  const content = getBannerContent();

  const handleCTAClick = () => {
    if (user && !isPro) {
      // Free user, redirect to upgrade page
      window.location.href = '/upgrade';
    } else {
      // Unauthenticated user, open signup modal
      setModalOpen(true);
    }
  };

  return (
    <>
      <div className={`${content.bgColor} ${content.borderColor} border-b sticky top-0 z-40`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <Sparkles className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <div className={`text-sm font-medium ${content.textColor}`}>
                  {content.title}
                  {content.subtitle && (
                    <span className="hidden sm:inline ml-1">{content.subtitle}</span>
                  )}
                </div>
                {/* Mobile subtitle */}
                {content.subtitle && (
                  <div className={`text-xs ${content.textColor} opacity-80 sm:hidden`}>
                    {content.subtitle}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                onClick={handleCTAClick}
                variant="primary"
                size="sm"
                className={content.buttonColor}
              >
                {content.ctaText}
              </Button>
              
              <Button
                onClick={() => setIsDismissed(true)}
                variant="ghost"
                size="sm"
                className={`${content.textColor} hover:opacity-70 p-1 h-8 w-8 min-w-0`}
                leftIcon={X}
                aria-label="Dismiss banner"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Signup Modal */}
      <WaitlistModal 
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        triggerAction="banner_cta"
      />
    </>
  );
};

export default BetaBanner;